import { quotedItems, proposalLineKey, formatWarranty } from "../../constants"
import { formatVnd } from "procurement/design/format/money"

export const hasValue = (v) => v != null && String(v).trim() !== ""

// Fixed criteria of the vendor compare/scoring block (B5). `get(p, ctx)` is the
// display value (blank → "-"); `defaultField` names the key in `p.default`;
// `ok` overrides "filled in" when the value is always present. `key` goes to
// BE in scoring.vendors[].ticked — keep it stable.
export const CRITERIA_ROWS = [
  {
    key: "fullPrice",
    label: "Full price, all lines",
    get: (p, { lineKeys }) => {
      const priced = quotedItems(p).filter((it) => lineKeys.includes(proposalLineKey(it))).length
      return `${priced}/${lineKeys.length} lines priced`
    },
    ok: (p, { lineKeys }) => lineKeys.every((k) => quotedItems(p).some((it) => proposalLineKey(it) === k)),
  },
  { key: "taxCode", label: "Tax code", get: (p) => p.proposalTaxCode, defaultField: "proposalTaxCode" },
  { key: "address", label: "Address", get: (p) => p.proposalAddress, defaultField: "proposalAddress" },
  { key: "contact", label: "Contact person", get: (p) => p.vendorContactName },
  { key: "phone", label: "Phone", get: (p) => p.vendorContactPhone },
  { key: "email", label: "Email", get: (p) => p.vendorContactEmail, defaultField: "vendorContactEmail" },
  { key: "alternative", label: "Alternative offer", get: (p) => p.alternative },
  { key: "extraInfo", label: "Other terms", get: (p) => p.extraInfo },
  {
    key: "warranty",
    label: "Warranty",
    get: (p) => [...new Set(quotedItems(p).map(formatWarranty).filter(Boolean))].join(", "),
  },
  {
    key: "deliveryFee",
    label: "Delivery fee",
    get: (p) => (p.deliveryFee != null ? Number(p.deliveryFee).toLocaleString("vi-VN") : null),
  },
  {
    key: "bank",
    label: "Bank account",
    get: (p) => [p.supplier?.bankName, p.supplier?.bankAccount].filter(Boolean).join(" · "),
  },
]

// Panel/modal `sourceType` → GET/POST /proposal-comparisons `scopeType`.
export const COMPARISON_SCOPE_TYPE = { order: "order_request", group: "group_order", purchase: "purchase" }

export const criteriaKey = (id) => `criteria:${id}`

export const requirementNames = (criteria = []) =>
  [...criteria].sort((a, b) => a.orderNo - b.orderNo).map((c) => c.name)

// Fixed rows + one row per OR/PO requirement (GET /proposal-comparisons
// `criteria[]`); the cell is the vendor's free-text answer to it.
export const buildCriteriaRows = (criteria = []) => [
  ...CRITERIA_ROWS,
  ...[...criteria]
    .sort((a, b) => a.orderNo - b.orderNo)
    .map((c) => ({ key: criteriaKey(c.id), label: c.name, get: (p) => p.requirementAnswers?.[c.id] })),
]

// Supplier-master fallback (`p.default`, only on GET /proposal-comparisons),
// shown as "default: …" when the vendor left the field blank.
export const defaultValue = (row, p) => (row.defaultField ? p.default?.[row.defaultField] : null)

export const criterionOk = (row, p, ctx) =>
  row.ok ? row.ok(p, ctx) : hasValue(row.get(p, ctx)) || hasValue(defaultValue(row, p))

const formatAt = (d) => {
  const pad = (x) => String(x).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// The round's `noteText`, stored as-is by BE so any screen can print it raw:
//   [Chấm điểm] a@x.com · 23/10/2026 09:12
//   • [Preferred] NCC A (#12): 8/11 | ✓ Tax code | - Address | …
//     Laptop Dell - 15.000.000 ₫
//     Chuột - 200.000 ₫
//   Chọn: NCC A — Giá tốt nhất
// `vendors`: [{ id, name, ticked: Set<criteriaKey>, preferred: bool, lines: [{ name, amount }] }]
// `chosen`: { name, reason } | null
// `rows`: buildCriteriaRows(criteria)
export const formatScoring = ({ by, at = new Date(), rows = CRITERIA_ROWS, vendors, chosen }) =>
  [
    `[Chấm điểm] ${by || "unknown"} · ${formatAt(at)}`,
    ...vendors.flatMap((v) => {
      const marks = rows.map((r) => `${v.ticked.has(r.key) ? "✓" : "-"} ${r.label}`)
      const tag = v.preferred ? "[Preferred] " : ""
      return [
        `• ${tag}${v.name} (#${v.id}): ${v.ticked.size}/${rows.length} | ${marks.join(" | ")}`,
        ...v.lines.map((l) => `  ${l.name} - ${formatVnd(l.amount)}`),
      ]
    }),
    ...(chosen ? [`Chọn: ${chosen.name}${chosen.reason ? ` — ${chosen.reason}` : ""}`] : []),
  ].join("\n")
